import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../design-system/icons.jsx';

const CONDITIONS = ['LIKE', '=', '>', '<', 'IS NULL'];

export default function TextFilter({ value, onChange }) {
  const { t } = useTranslation();

  const LIKE_MODES = [
    { key: 'contains', label: t('table.containsMode') },
    { key: 'begins',   label: t('table.beginsWith') },
    { key: 'ends',     label: t('table.endsWith') },
  ];

  const not       = value?.not       ?? false;
  const condition = value?.condition ?? 'LIKE';
  const likeMode  = value?.likeMode  ?? 'contains';
  const val       = value?.value     ?? '';
  const order     = value?.order     ?? null;

  const update = (patch) =>
    onChange({ not, condition, likeMode, value: val, order, ...patch });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Condition */}
      <div>
        <div style={S.label}>{t('table.selectCondition')}</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" onClick={() => update({ not: !not })} style={{
            ...S.btn,
            background: not ? 'color-mix(in srgb, var(--danger) 12%, transparent)' : 'transparent',
            color: not ? 'var(--danger)' : 'var(--text-secondary)',
            border: `1px solid ${not ? 'var(--danger)' : 'var(--border)'}`,
          }}>{t('table.notLabel')}</button>
          {CONDITIONS.map(c => (
            <button key={c} type="button" onClick={() => update({ condition: c })} style={{
              ...S.btn,
              background: condition === c ? 'var(--accent)' : 'transparent',
              color: condition === c ? '#fff' : 'var(--text-secondary)',
              border: `1px solid ${condition === c ? 'var(--accent)' : 'var(--border)'}`,
            }}>{c}</button>
          ))}
        </div>
      </div>

      {/* LIKE sub-mode */}
      {condition === 'LIKE' && (
        <div style={{ display: 'flex', gap: 4 }}>
          {LIKE_MODES.map(m => (
            <button key={m.key} type="button" onClick={() => update({ likeMode: m.key })} style={{
              ...S.btn, flex: 1, textAlign: 'center',
              background: likeMode === m.key ? 'var(--accent)' : 'var(--bg-elevated)',
              color: likeMode === m.key ? '#fff' : 'var(--text-secondary)',
              border: `1px solid ${likeMode === m.key ? 'var(--accent)' : 'var(--border)'}`,
            }}>{m.label}</button>
          ))}
        </div>
      )}

      {/* Value input */}
      {condition !== 'IS NULL' && (
        <div>
          <div style={S.label}>{t('table.enterValue')}</div>
          <input
            autoFocus
            type="text"
            value={val}
            onChange={e => update({ value: e.target.value })}
            placeholder={t('table.search')}
            style={S.input}
          />
        </div>
      )}

      {/* Sort order */}
      <div>
        <div style={S.label}>{t('table.selectOrder')}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[{ dir: 'asc', icon: 'arrowUp' }, { dir: 'desc', icon: 'arrowDown' }].map(({ dir, icon }) => (
            <button key={dir} type="button"
              onClick={() => update({ order: order === dir ? null : dir })}
              style={{
                ...S.btn,
                width: 36, height: 32, padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15,
                background: 'var(--bg-elevated)',
                color: order === dir ? 'var(--text-primary)' : 'var(--text-muted)',
                border: `1px solid ${order === dir ? 'var(--accent)' : 'var(--border)'}`,
              }}
            >{<Icon name={icon} size={16} />}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

const S = {
  label: {
    fontSize: 10, color: 'var(--text-muted)', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
  },
  btn: {
    padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 5,
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.12s',
  },
  input: {
    background: 'var(--bg-elevated)', border: '1px solid var(--border)',
    borderRadius: 6, padding: '6px 10px', color: 'var(--text-primary)',
    fontSize: 12, fontFamily: 'var(--font-mono)', outline: 'none',
    width: '100%', boxSizing: 'border-box',
  },
};
